package com.shadowings.gero.presentation.component.quiz

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.semantics.testTag
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp

/**
 * Shared question/options two-pane layout used by every answerable quiz type
 * (single/multi selection, true/false, multi-press, monoliteral, word
 * selection, text translation): a question area and an options/answer area,
 * side by side in landscape, stacked in portrait, with the bottom feedback
 * button reserved for via [getBottomButtonHeight]/[getBottomButtonPadding].
 *
 * The default parameter values reproduce the original hand-copied scaffold
 * exactly (as it existed in MultiSelectionComposable/SingleSelectionComposable/
 * MultiPressComposable). Other quiz types override individual axes (weight,
 * arrangement, scrollability, padding) to preserve their pre-existing visual
 * behavior instead of forking the whole layout again.
 */
@Composable
fun QuizContentScaffold(
    testTag: String,
    isLandscape: Boolean,
    modifier: Modifier = Modifier,
    landscapeQuestionArrangement: Arrangement.Vertical = Arrangement.Center,
    landscapeQuestionScrollable: Boolean = true,
    landscapeQuestionFillHeight: Boolean = false,
    landscapeQuestionRowAlignment: Alignment.Vertical = Alignment.CenterVertically,
    landscapeOptionsWeight: Float = 1f,
    landscapeOptionsArrangement: Arrangement.Vertical = Arrangement.spacedBy(8.dp),
    landscapeOptionsHorizontalAlignment: Alignment.Horizontal = Alignment.Start,
    landscapeOptionsHorizontalPadding: Dp = 16.dp,
    landscapeOptionsScrollable: Boolean = true,
    landscapeOptionsRowAlignment: Alignment.Vertical = Alignment.CenterVertically,
    portraitOptionsArrangement: Arrangement.Vertical = Arrangement.spacedBy(8.dp, Alignment.CenterVertically),
    portraitOptionsScrollable: Boolean = true,
    portraitOptionsHorizontalPadding: Dp = 16.dp,
    portraitSpacerBeforeOptions: Dp = 0.dp,
    questionContent: @Composable ColumnScope.() -> Unit,
    optionsContent: @Composable ColumnScope.() -> Unit,
) {
    Column(
        modifier
            .fillMaxSize()
            .semantics { this.testTag = testTag },
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.SpaceBetween
    ) {
        if (isLandscape) {
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(bottom = getBottomButtonHeight() + getBottomButtonPadding() * 2)
                    .weight(1f),
                horizontalArrangement = Arrangement.spacedBy(16.dp),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Column(
                    modifier = Modifier
                        .weight(1f)
                        .then(if (landscapeQuestionFillHeight) Modifier.fillMaxHeight() else Modifier)
                        .then(if (landscapeQuestionScrollable) Modifier.verticalScroll(rememberScrollState()) else Modifier)
                        .align(landscapeQuestionRowAlignment),
                    horizontalAlignment = Alignment.CenterHorizontally,
                    verticalArrangement = landscapeQuestionArrangement
                ) {
                    questionContent()
                }

                Column(
                    modifier = Modifier
                        .weight(landscapeOptionsWeight)
                        .padding(horizontal = landscapeOptionsHorizontalPadding)
                        .then(if (landscapeOptionsScrollable) Modifier.verticalScroll(rememberScrollState()) else Modifier)
                        .align(landscapeOptionsRowAlignment),
                    horizontalAlignment = landscapeOptionsHorizontalAlignment,
                    verticalArrangement = landscapeOptionsArrangement
                ) {
                    optionsContent()
                }
            }
        } else {
            Column(
                Modifier
                    .fillMaxWidth()
                    .weight(1f),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                questionContent()
                if (portraitSpacerBeforeOptions > 0.dp) {
                    Spacer(Modifier.height(portraitSpacerBeforeOptions))
                }
                Column(
                    modifier = Modifier
                        .fillMaxWidth()
                        .weight(1f)
                        .padding(horizontal = portraitOptionsHorizontalPadding)
                        .then(if (portraitOptionsScrollable) Modifier.verticalScroll(rememberScrollState()) else Modifier),
                    verticalArrangement = portraitOptionsArrangement
                ) {
                    optionsContent()
                }
                Spacer(Modifier.height(getBottomButtonHeight() + getBottomButtonPadding() * 2))
            }
        }
    }
}
